import { useEffect, useState } from 'react';

import { getCollection } from '../services/collections';
import { collectionOffersCard } from '../utils/lookupScope';
import { normalizeId } from '../utils/ids';
import { useOptionalCardDefinitions } from '../contexts/CardDefinitionsContext';
import { getSessionKey, onSessionChange } from '../utils/session';

// One read per collection per account, refreshed after STALE_MS. An entry
// carries the session it was read under and is never served to another;
// a failed read is forgotten.
const STALE_MS = 60 * 1000;
const reads = new Map();
onSessionChange(() => { reads.clear(); });
const read = (id) => {
	const hit = reads.get(id);
	if (hit && hit.owner === getSessionKey() && Date.now() - hit.at < STALE_MS) return hit.promise;
	const entry = { at: Date.now(), owner: getSessionKey(), promise: null };
	entry.promise = getCollection(id).catch(() => {
		if (reads.get(id) === entry) reads.delete(id);
		return null;
	});
	reads.set(id, entry);
	return entry.promise;
};

/**
 * Whether a collection holds a card a button or layout block names
 * (KPR-237 G12/G13): a global card may name another global card by id, and
 * where the collection does not hold that card the door is not offered.
 *
 * Returns `(cardId) => boolean`. It fails OPEN: while the collection or the
 * card list is not in hand, or when either cannot be read, everything is
 * offered, and the API's own refusal stands. Nothing is fetched unless
 * `enabled`.
 */
export default function useCollectionOffersCard(collectionId, enabled = true) {
	const id = normalizeId(collectionId);
	const [collection, setCollection] = useState(null);
	const cards = useOptionalCardDefinitions()?.cards;
	useEffect(() => {
		setCollection(null);
		if (!enabled || !id) return undefined;
		let isMounted = true;
		read(id).then((doc) => { if (isMounted) setCollection(doc || null); });
		return () => { isMounted = false; };
	}, [id, enabled]);
	return (cardId) => {
		if (!enabled || !collection || !Array.isArray(cards) || !cards.length) return true;
		return collectionOffersCard(collection, cardId, cards);
	};
}
